import { Inbox } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCOIEmailThread, useCOIEmailThreads } from '../../../../models/coiEmail'
import { EmailThreadList } from '../Internal/Emails/EmailThreadList'
import { EmailThreadView } from '../Internal/Emails/EmailThreadView'

/**
 * Every certificate request and send for one org, threaded.
 *
 * The same reader the internal inbox uses, narrowed to this org's COI mail, so
 * whoever runs a carrier's certificates can see what came in and what went out
 * without paging through everybody else's email.
 */
export function COIEmailsTab({ orgId }: { orgId?: string }) {
  const [q, setQ] = useState('')
  const [needsAttention, setNeedsAttention] = useState(false)
  const [page, setPage] = useState(1)
  const [threadKey, setThreadKey] = useState<string | null>(null)

  const { data, isLoading } = useCOIEmailThreads({ q, needsAttention, page }, orgId)
  const { data: thread, isLoading: threadLoading } = useCOIEmailThread(threadKey, orgId)
  const attentionCount = data?.needsAttentionCount ?? 0

  // A filter change reshuffles the pages, so the old page number stops meaning
  // anything — and the open thread may not be in the new set.
  function refilter(change: () => void) {
    change()
    setPage(1)
    setThreadKey(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={q}
          onChange={(event) => refilter(() => setQ(event.target.value))}
          placeholder="Search subject, sender or message…"
          className="max-w-xs"
        />
        <Button
          variant={needsAttention ? 'default' : 'outline'}
          onClick={() => refilter(() => setNeedsAttention(!needsAttention))}
        >
          <Inbox className="size-4" />
          Needs attention
          {attentionCount > 0 && ` (${attentionCount})`}
        </Button>
        {needsAttention && (
          <span className="text-muted-foreground text-sm">
            Requests we could not place, and questions nobody has answered.
          </span>
        )}
      </div>

      {/* Capped, not fixed: a handful of threads should not leave a screen of
          empty box below them, and a full page still scrolls inside the cap. */}
      <div className="grid max-h-[calc(100dvh-20rem)] min-h-75 grid-cols-1 gap-4 lg:grid-cols-[minmax(18rem,24rem)_1fr]">
        <EmailThreadList
          page={data}
          isLoading={isLoading}
          pageNumber={page}
          selectedKey={threadKey}
          onSelect={setThreadKey}
          onPage={setPage}
          showOrg={false}
        />
        {/* Narrow screens have one column, so the reader only earns its space
            once a thread is chosen — hiding it outright made selecting one do
            nothing visible. */}
        <div className={threadKey ? 'block' : 'hidden lg:block'}>
          <EmailThreadView threadKey={threadKey} thread={thread} isLoading={threadLoading} />
        </div>
      </div>
    </div>
  )
}
